import React from "react";
import {
  Box,
  Typography,
  Stack,
  Menu,
  MenuItem,
  Divider,
  Card,
  CardContent,
  Paper,
  IconButton,
  useTheme,
} from "@mui/material";
import { consola } from "consola";
import moment from "moment";
import { useConfirm } from "material-ui-confirm";
import { useRecoilValue } from "recoil";
import { styled } from "@mui/material/styles";
import {
  Description as FileIcon,
  MoreVert as MoreVertIcon,
  InsertDriveFile as DocumentIcon,
} from "@mui/icons-material";
import StandardButton from "../StandardButton";
import useSharedFiles from "../../hooks/useSharedFiles";
import organizationIdSelector from "../../atoms/organizationIdSelector";
import useEvents from "../../hooks/useEvents";

// Styled components for consistency with other enhanced tabs
const StyledFilesCard = styled(Card)(({ theme }) => ({
  height: "100%", // Changed from "auto" to "100%" to fill container
  display: "flex", // Add flex display
  flexDirection: "column", // Stack header and content
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(2),
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  transition: "box-shadow 0.3s ease",
  "&:hover": {
    boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
  },
}));

const FilesHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5), // Reduced from 2 to 1.5
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
}));

const FilesContent = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(1.5), // Reduced from 2 to 1.5
  paddingTop: theme.spacing(1), // Keep this minimal
  flex: 1,
  overflow: "auto", // Enable scrolling within the card content
  minHeight: 0, // Important for flex containers to allow shrinking
}));



// File type helper function
const getFileTypeIcon = (filename) => {
  const ext = filename.split(".").pop().toLowerCase();

  if (["pdf"].includes(ext)) {
    return <DocumentIcon sx={{ color: "#e53935" }} />;
  } else if (["doc", "docx", "txt", "rtf"].includes(ext)) {
    return <DocumentIcon sx={{ color: "#1565c0" }} />;
  } else if (["xls", "xlsx", "csv"].includes(ext)) {
    return <DocumentIcon sx={{ color: "#2e7d32" }} />;
  } else if (["ppt", "pptx"].includes(ext)) {
    return <DocumentIcon sx={{ color: "#ff8f00" }} />;
  } else if (["mp4", "mov"].includes(ext)) {
    return <DocumentIcon sx={{ color: "#6a1b9a" }} />;
  } else if (["mp3"].includes(ext)) {
    return <DocumentIcon sx={{ color: "#0097a7" }} />;
  } else if (["zip", "rar"].includes(ext)) {
    return <DocumentIcon sx={{ color: "#616161" }} />;
  }

  return <FileIcon />;
};

function FileMenuItem({ key, menuItem, setAnchorEl }) {
  return (
    <MenuItem
      key={key}
      onClick={() => {
        setAnchorEl(null);
        if (menuItem.menuFunction) {
          menuItem.menuFunction(menuItem.file);
        }
      }}
    >
      {`${menuItem.menuName}`}
    </MenuItem>
  );
}

function FileMenu({ uid, anchorEl, setAnchorEl, handleClose, onDelete }) {
  const confirm = useConfirm();
  const events = useEvents();

  function confirmDelete() {
    confirm({
      description: `Are you sure you want to delete this file?`,
    })
      .then(() => {
        if (onDelete) {
          onDelete();
        }
        const logEvent = {
          buttonName: "Delete File",
          pageName: "Clients",
          tabName: uid,
        };
        events.logButtonClick(logEvent);
      })
      .catch(() => {
        consola.log("delete file cancelled");
      });
  }

  const menuItems = [
    {
      id: "delete",
      type: "menu",
      menuName: "Delete File",
      menuFunction: confirmDelete,
    },
  ];

  return (
    <Menu
      id="simple-menu"
      anchorEl={anchorEl}
      keepMounted
      open={Boolean(anchorEl)}
      onClose={handleClose}
    >
      {menuItems &&
        menuItems.map((menuItem, index) => {
          if (menuItem.type === "menu") {
            return (
              <FileMenuItem
                key={menuItem.id}
                menuItem={menuItem}
                setAnchorEl={setAnchorEl}
              />
            );
          } else {
            return <Divider key={index} />;
          }
        })}
    </Menu>
  );
}

function MenuIcon({ file, onClick }) {
  return (
    <IconButton
      size="small"
      onClick={(event) => {
        onClick(file, event);
      }}
    >
      <MoreVertIcon />
    </IconButton>
  );
}

function SharedFiles({ uid }) {
  const theme = useTheme();
  const organizationId = useRecoilValue(organizationIdSelector);
  const events = useEvents();
  const { sharedFiles, uploadFile, deleteFile } = useSharedFiles({
    uid: localStorage.getItem("uid"),
    clientUID: uid,
    organizationId,
  });
  const [anchorEl, setAnchorEl] = React.useState(null);
  const currentFile = React.useRef(null);

  const fileInputRef = React.useRef(null);

  const handleClick = (file, event) => {
    currentFile.current = file;
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  if (!sharedFiles) {
    return null;
  }

  return (
    <Box
      sx={{
        p: 1.5, // Reduced from 2 to 1.5 for even more space
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <input
        ref={fileInputRef}
        hidden={true}
        type="file"
        accept=".pdf,.csv,.doc,.docx,.txt,.xls,.xlsx,.zip,.rtf,.rar,.ppt,.pptx,.mp4,.mp3,.mov"
        onChange={(e) => {
          const logEvent = {
            buttonName: "Upload File",
            pageName: "Clients",
            tabName: uid,
          };
          events.logButtonClick(logEvent);
          uploadFile({ file: e.target.files[0], isCoach: true });
          e.target.value = null;
        }}
      />

      <StyledFilesCard>
        <FilesHeader>
          <Stack direction="row" alignItems="center" spacing={1}>
            <FileIcon color="primary" sx={{ fontSize: 24 }} />
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ fontSize: "1.1rem" }}
            >
              Shared Files
            </Typography>
          </Stack>

          {/* Upload button moved to header for better space utilization */}
          {sharedFiles.length > 0 && (
            <StandardButton
              variant="secondary"
              onClick={() => {
                fileInputRef.current.click();
              }}
              sx={{
                borderRadius: "20px",
                padding: "6px 12px", // Reduced padding for narrow modal
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.875rem", // Smaller font size
              }}
            >
              Upload
            </StandardButton>
          )}
        </FilesHeader>

        <FilesContent>
          {sharedFiles.length === 0 ? (
            <Paper
              sx={{
                p: 3,
                textAlign: "center",
                bgcolor: "grey.50",
                borderStyle: "dashed",
                borderWidth: 2,
                borderColor: "grey.300",
              }}
            >
              <FileIcon sx={{ fontSize: 48, color: "grey.400", mb: 2 }} />
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                There are no files shared by your client. Files will appear here
                when your client shares files with you. You can also share files
                with your clients.
              </Typography>
              <StandardButton
                variant="secondary"
                onClick={() => {
                  fileInputRef.current.click();
                }}
                sx={{
                  borderRadius: "20px",
                  padding: "8px 16px", // Slightly reduced padding
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                Upload a File
              </StandardButton>
            </Paper>
          ) : (
            <Stack spacing={1}>
              {sharedFiles.map((file) => (
                <Paper
                  key={file.id}
                  elevation={0}
                  sx={{
                    p: 1.5, // Reduced from 2 to 1.5 for more compact items
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    "&:hover": {
                      bgcolor: "rgba(0, 0, 0, 0.04)",
                    },
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Box sx={{ flexShrink: 0 }}>
                      {getFileTypeIcon(file.name)}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <a
                        style={{
                          color: theme.palette.secondary.main,
                          textDecorationLine: "underline",
                          fontSize: "0.875rem",
                          fontWeight: 500,
                          display: "block",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        href={file.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={file.name}
                      >
                        {file.name}
                      </a>
                      <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          {moment(file.createdOn.toDate()).format(
                            "MMM D, YYYY",
                          )}
                        </Typography>
                        {file.metadata && file.metadata.sharedBy && (
                          <Typography variant="caption" color="text.secondary">
                            By {file.metadata.sharedBy.firstName}
                          </Typography>
                        )}
                      </Stack>
                    </Box>
                    <Box sx={{ flexShrink: 0 }}>
                      <MenuIcon
                        file={file}
                        onClick={(file, event) => {
                          handleClick(file, event);
                        }}
                      />
                    </Box>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          )}
        </FilesContent>
      </StyledFilesCard>

      <FileMenu
        uid={uid}
        anchorEl={anchorEl}
        onDelete={() => {
          deleteFile({ id: currentFile.current.id });
        }}
        setAnchorEl={setAnchorEl}
        handleClose={handleClose}
      />
    </Box>
  );
}

export default SharedFiles;
